import React, { useState, useEffect } from "react";
import { Post, PostStatus } from "@/core/types/postTypes";
import { postsService } from "../services/postsService";
import { ClientContext } from "@/contexts/ClientContext";
import { PostItem } from "@/core/Post/PostItem.model";
import { UserRole } from "@/services/urlService";
import { usePostCardAuthOptional } from "../contexts/PostCardAuthContext";
import { postCacheUtils } from "../hooks/usePostsQuery";

interface PostPreparationBadgeProps {
  post: Post;
  clientId?: string;
}

export const PostPreparationBadge: React.FC<PostPreparationBadgeProps> = ({ post, clientId: providedClientId }) => {
  const authContext = usePostCardAuthOptional();
  const clientContext = React.useContext(ClientContext);
  const selectedClient = clientContext?.selectedClient;
  const clientId = providedClientId || selectedClient?.id;

  const [isRetrying, setIsRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localStatus, setLocalStatus] = useState<string | null>(null);

  // Use local status if set (optimistic update), otherwise use post status
  const status = localStatus || post.postFolderPreparationStatus || 'pending';
  const isPublishableStatus =
    post.status === PostStatus.READY ||
    post.status === PostStatus.SCHEDULED ||
    post.status === PostStatus.PARTIALLY_PUBLISHED;

  // Clear local status when post status updates from backend (via SSE)
  useEffect(() => {
    if (localStatus && post.postFolderPreparationStatus !== localStatus) {
      setLocalStatus(null);
      setIsRetrying(false);
    }
  }, [post.postFolderPreparationStatus, localStatus]);

  // Hide for client role (must be after all hooks)
  if (authContext?.userRole === UserRole.CLIENT) {
    return null;
  }

  const handleRetryPreparation = async () => {
    if (!clientId) return;

    setIsRetrying(true);
    setError(null);

    // Optimistically set to preparing
    setLocalStatus('preparing');

    try {
      const response = await postsService.retryPostPreparation(clientId, post.id);

      // Update React Query cache with the returned post data
      if (response.post) {
        const updatedPost = PostItem.fromLegacyPost(response.post, UserRole.ADMIN, clientId);
        postCacheUtils.upsertPost(clientId, updatedPost);
      }

      // Keep showing "preparing" state
      // SSE will update when backend status changes
    } catch (error) {
      setError(error instanceof Error ? error.message : "Failed to retry preparation");
      setIsRetrying(false);
      setLocalStatus(null); // Reset optimistic update on error
    }
  };

  // Always show badge for publishable statuses
  if (!isPublishableStatus) {
    return null;
  }

  // Show scheduled time when it exists (independent of status)
  const scheduledTimeBadge = post.scheduledAt ? (
    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
      <svg
        className="w-3.5 h-3.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
      Scheduled: {new Date(post.scheduledAt).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })}
    </div>
  ) : null;

  // Ready state - show scheduled time only
  if (status === 'ready') {
    if (!scheduledTimeBadge) return null;
    return (
      <div className="inline-flex flex-col gap-2">
        {scheduledTimeBadge}
      </div>
    );
  }

  // Failed state
  if (status === 'failed') {
    return (
      <div className="inline-flex flex-col gap-1">
        {scheduledTimeBadge}
        <div className="inline-flex items-center gap-2">
          <div
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium${
              post.postFolderPreparationError ? " cursor-help" : ""
            }`}
            title={post.postFolderPreparationError || undefined}
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            Preparation Failed
          </div>
          <button
            onClick={handleRetryPreparation}
            disabled={isRetrying}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-full text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg
              className={`w-3.5 h-3.5 ${isRetrying ? 'animate-spin' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {isRetrying ? 'Retrying...' : 'Retry'}
          </button>
        </div>
        {error && (
          <div className="text-xs text-red-600">{error}</div>
        )}
      </div>
    );
  }

  // Pending state - not yet triggered
  if (status === 'pending') {
    return (
      <div className="inline-flex flex-col gap-1">
        {scheduledTimeBadge}
        <div className="inline-flex items-center gap-2">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Not Prepared
          </div>
          <button
            onClick={handleRetryPreparation}
            disabled={isRetrying}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-full text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg
              className={`w-3.5 h-3.5 ${isRetrying ? 'animate-spin' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {isRetrying ? 'Starting...' : 'Prepare'}
          </button>
        </div>
        {error && (
          <div className="text-xs text-red-600">{error}</div>
        )}
      </div>
    );
  }

  // Preparing state (actively processing)
  return (
    <div className="inline-flex flex-col gap-1">
      {scheduledTimeBadge}
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
        <svg
          className="w-3.5 h-3.5 animate-spin"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        Preparing...
      </div>
    </div>
  );
};
